import React, { useEffect, useMemo, useState } from 'react';
import {
  SparklesIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PlusIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  BookmarkSquareIcon,
  TrashIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

import type {
  Candidate,
  Criterion,
  Evaluation,
  EvaluationStatus,
  UploadedFile,
  Citation,
} from '../types';
import { formatRelative } from '../utils';

interface Props {
  candidate: Candidate | null;
  criterion: Criterion | null;
  evaluation: Evaluation | undefined;
  citations: Citation[]; // toutes les citations du projet
  files: UploadedFile[];
  criteriaValidated: boolean;
  referenceNow?: string;
  activeCitationId: string | null;
  hoveredCitationId: string | null;
  onSelectCitation: (id: string | null) => void;
  onHoverCitation: (id: string | null) => void;
  onSaveEvaluation: (update: Partial<Evaluation>) => void;
  onVerifyCitation: (citationId: string, verified: boolean) => void;
  onRemoveCitation: (citationId: string) => void;
  onAddCitation: (text: string, sourceFileId: string, sourcePage: number) => void;
  onOpenCriteria?: () => void;
}

const STATUS_CFG: Record<
  EvaluationStatus,
  { label: string; classes: string; icon: React.ReactNode }
> = {
  conforme: {
    label: 'Conforme',
    classes: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    icon: <CheckCircleIcon className="w-4 h-4" />,
  },
  partial: {
    label: 'Partiel',
    classes: 'text-amber-700 bg-amber-50 border-amber-200',
    icon: <ExclamationTriangleIcon className="w-4 h-4" />,
  },
  non_conforme: {
    label: 'Non conforme',
    classes: 'text-red-700 bg-red-50 border-red-200',
    icon: <XCircleIcon className="w-4 h-4" />,
  },
};

/**
 * Panneau de droite du workspace : édition de l'évaluation (candidat × critère).
 *
 * Sections empilées verticalement (pas de tabs) :
 *  1. Score IA vs Ma note — badges + call-to-action "Copier IA"
 *  2. Citations — extraits du mémoire, V/R, ajout manuel
 *  3. Analyse IA — points forts / faibles / synthèse
 *  4. Ma validation — note finale + statut + commentaire + bouton Valider
 *
 * Tant que `criteriaValidated === false`, l'ensemble est désactivé et un
 * bandeau invite l'analyste à valider les critères d'abord.
 */
const EvaluationEditor: React.FC<Props> = ({
  candidate,
  criterion,
  evaluation,
  citations,
  files,
  criteriaValidated,
  referenceNow,
  activeCitationId,
  hoveredCitationId,
  onSelectCitation,
  onHoverCitation,
  onSaveEvaluation,
  onVerifyCitation,
  onRemoveCitation,
  onAddCitation,
  onOpenCriteria,
}) => {
  const [userScore, setUserScore] = useState<string>('');
  const [userStatus, setUserStatus] = useState<EvaluationStatus | ''>('');
  const [userComment, setUserComment] = useState<string>('');

  // Sync des inputs quand la cellule change
  useEffect(() => {
    if (!evaluation) {
      setUserScore('');
      setUserStatus('');
      setUserComment('');
      return;
    }
    setUserScore(
      evaluation.userScore !== undefined
        ? String(evaluation.userScore)
        : evaluation.aiScore !== undefined
        ? String(evaluation.aiScore)
        : ''
    );
    setUserStatus(evaluation.userStatus ?? evaluation.aiStatus ?? '');
    setUserComment(evaluation.userComment ?? '');
  }, [evaluation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cellCitations = useMemo(
    () => (evaluation ? citations.filter(c => c.evaluationId === evaluation.id) : []),
    [evaluation, citations]
  );

  const weight = criterion?.weight ?? 0;
  // Critère financier : pas d'IA, pas de citation, note saisie directement.
  // Le score est typiquement calculé depuis une formule de prix et l'analyste
  // le reporte manuellement (c'est hors du champ de l'analyse technique).
  const isFinancial = criterion?.type === 'financial';
  const aiScore = isFinancial ? undefined : evaluation?.aiScore;
  const aiStatus = isFinancial ? undefined : evaluation?.aiStatus;
  const strengths = isFinancial ? [] : evaluation?.aiStrengths ?? [];
  const weaknesses = isFinancial ? [] : evaluation?.aiWeaknesses ?? [];
  const justification = isFinancial ? '' : evaluation?.aiJustification ?? '';

  const canSave = criteriaValidated && criterion !== null && candidate !== null;

  const handleSave = () => {
    if (!canSave) return;
    const scoreNum = userScore.trim() ? Number(userScore) : undefined;
    if (scoreNum !== undefined && (Number.isNaN(scoreNum) || scoreNum < 0)) return;
    onSaveEvaluation({
      userScore: scoreNum,
      userStatus: userStatus || undefined,
      userComment: userComment.trim() || undefined,
      userValidatedAt: new Date().toISOString(),
    });
  };

  const handleReset = () => {
    setUserScore('');
    setUserStatus('');
    setUserComment('');
    onSaveEvaluation({
      userScore: undefined,
      userStatus: undefined,
      userComment: undefined,
      userValidatedAt: undefined,
    });
  };

  if (!candidate || !criterion) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-white">
        <CheckBadgeIcon className="w-12 h-12 text-gray-300 mb-3" />
        <p className="text-sm text-gray-600">
          Choisissez un candidat et un critère pour commencer à noter.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header : critère courant */}
      <header className="shrink-0 px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide tabular-nums">
            {criterion.code}
          </span>
          <span className="text-[11px] font-medium text-gray-500">
            {weight} pt{weight > 1 ? 's' : ''}
          </span>
          {criterion.type === 'financial' && (
            <span className="text-[10px] font-medium text-gray-600 uppercase tracking-wide px-1.5 py-0.5 bg-gray-100 rounded">
              financier
            </span>
          )}
        </div>
        <h2 className="text-base font-semibold text-gray-900 leading-snug">{criterion.title}</h2>
        {criterion.description && (
          <p className="mt-1 text-xs text-gray-600 leading-relaxed">{criterion.description}</p>
        )}

        {/* Badges de score — l'IA est masquée pour les critères financiers */}
        <div className="mt-3 flex items-center gap-2">
          {!isFinancial && (
            <ScoreBadge
              label="IA"
              icon={<SparklesIcon className="w-3.5 h-3.5" />}
              score={aiScore}
              weight={weight}
              status={aiStatus}
              tone="ai"
            />
          )}
          <ScoreBadge
            label="Ma note"
            icon={<CheckBadgeIcon className="w-3.5 h-3.5" />}
            score={evaluation?.userScore}
            weight={weight}
            status={evaluation?.userStatus}
            tone="user"
          />
        </div>
      </header>

      {/* Bandeau verrouillage */}
      {!criteriaValidated && (
        <div className="shrink-0 px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2">
          <LockClosedIcon className="shrink-0 w-4 h-4 text-amber-700 mt-0.5" />
          <div className="text-xs text-amber-900">
            La notation est désactivée tant que les critères ne sont pas validés.{' '}
            {onOpenCriteria && (
              <button
                type="button"
                onClick={onOpenCriteria}
                className="font-medium underline hover:text-amber-950"
              >
                Valider les critères →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Corps scrollable : sections empilées */}
      <div className="flex-1 overflow-y-auto">
        {/* ── Bandeau info pour les critères financiers ──── */}
        {isFinancial && (
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-start gap-2">
              <BanknotesIcon className="shrink-0 w-4 h-4 text-gray-500 mt-0.5" />
              <div className="text-xs text-gray-600 leading-relaxed">
                <span className="font-medium text-gray-800">Critère financier.</span>{' '}
                La note est reportée manuellement depuis la formule de prix. Pas d'analyse IA ni de citations pour ce critère.
              </div>
            </div>
          </div>
        )}

        {/* ── Citations (masquées pour les critères financiers) ─── */}
        {!isFinancial && (
        <section className="px-5 py-4 border-b border-gray-100">
          <SectionHeader
            icon={<DocumentTextIcon className="w-4 h-4" />}
            title="Citations"
            subtitle="Extraits retrouvés dans le mémoire, à vérifier d'un coup d'œil sur le PDF."
            count={cellCitations.length}
          />

          {cellCitations.length === 0 ? (
            <div className="mt-3 p-3 border border-dashed border-gray-200 rounded-lg bg-gray-50 text-center">
              <p className="text-xs text-gray-600">Aucune citation pour cette évaluation.</p>
            </div>
          ) : (
            <ul className="mt-3 space-y-2">
              {cellCitations.map(cit => {
                const file = files.find(f => f.id === cit.sourceFileId);
                const isActive = activeCitationId === cit.id;
                const isHovered = hoveredCitationId === cit.id;
                return (
                  <li
                    key={cit.id}
                    onMouseEnter={() => onHoverCitation(cit.id)}
                    onMouseLeave={() => onHoverCitation(null)}
                    onClick={() => onSelectCitation(cit.id)}
                    className={`border rounded-lg p-2.5 cursor-pointer transition-all ${
                      isActive
                        ? 'border-violet-400 bg-violet-50/50 ring-1 ring-violet-200'
                        : isHovered
                        ? 'border-violet-200 bg-violet-50/30'
                        : cit.verified
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : 'border-gray-200 bg-white hover:border-violet-200'
                    }`}
                  >
                    <p className="text-xs text-gray-800 leading-relaxed italic line-clamp-3">
                      «&nbsp;{cit.text}&nbsp;»
                    </p>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-600 min-w-0">
                        <span className="truncate max-w-[140px]" title={file?.name}>
                          {file?.name ?? 'Fichier supprimé'}
                        </span>
                        <span className="text-gray-400">·</span>
                        <span className="tabular-nums">p. {cit.sourcePage}</span>
                      </span>
                      <OriginBadge origin={cit.origin} />
                      <div className="ml-auto flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            onVerifyCitation(cit.id, !cit.verified);
                          }}
                          disabled={!criteriaValidated}
                          className={
                            cit.verified
                              ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
                              : 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
                          }
                          title={cit.verified ? 'Citation vérifiée (V)' : 'Marquer comme vérifiée (V)'}
                        >
                          <ShieldCheckIcon className="w-3 h-3" />
                          {cit.verified ? 'Vérifiée' : 'Vérifier'}
                        </button>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            onRemoveCitation(cit.id);
                          }}
                          disabled={!criteriaValidated}
                          className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Retirer (R)"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <AddCitationInline
            files={files.filter(f => f.candidateId === candidate.id)}
            onAdd={onAddCitation}
            disabled={!criteriaValidated}
          />
        </section>
        )}

        {/* ── Analyse IA (masquée pour les critères financiers) ─── */}
        {!isFinancial && (
        <section className="px-5 py-4 border-b border-gray-100">
          <SectionHeader
            icon={<SparklesIcon className="w-4 h-4" />}
            title="Analyse IA"
            subtitle={
              evaluation?.aiEvaluatedAt
                ? `Générée ${formatRelative(evaluation.aiEvaluatedAt, referenceNow)}`
                : 'Pas encore évalué par l\'IA.'
            }
          />

          {aiScore === undefined ? (
            <div className="mt-3 p-3 border border-dashed border-gray-200 rounded-lg bg-gray-50 text-center">
              <p className="text-xs text-gray-600">Aucune analyse IA disponible pour l'instant.</p>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              {justification && (
                <div>
                  <h4 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-1">
                    Synthèse
                  </h4>
                  <p className="text-xs text-gray-800 leading-relaxed">{justification}</p>
                </div>
              )}

              {strengths.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                    Points forts ({strengths.length})
                  </h4>
                  <ul className="space-y-1">
                    {strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-gray-800">
                        <CheckCircleIcon className="shrink-0 w-3.5 h-3.5 mt-0.5 text-emerald-600" />
                        <span className="leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {weaknesses.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1">
                    Points d'attention ({weaknesses.length})
                  </h4>
                  <ul className="space-y-1">
                    {weaknesses.map((w, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-gray-800">
                        <ExclamationTriangleIcon className="shrink-0 w-3.5 h-3.5 mt-0.5 text-amber-600" />
                        <span className="leading-relaxed">{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* ── Ma validation ─────────────────────────────── */}
        <section className="px-5 py-4">
          <SectionHeader
            icon={<CheckBadgeIcon className="w-4 h-4" />}
            title="Ma validation"
            subtitle={
              evaluation?.userValidatedAt
                ? `Dernière validation ${formatRelative(evaluation.userValidatedAt, referenceNow)}`
                : 'Définissez la note et le statut final qui feront foi.'
            }
          />

          <fieldset disabled={!criteriaValidated} className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="user-score"
                  className="block text-[11px] font-medium text-gray-700 mb-1"
                >
                  Note finale
                </label>
                <div className="relative">
                  <input
                    id="user-score"
                    type="number"
                    min="0"
                    max={weight}
                    step="0.1"
                    value={userScore}
                    onChange={e => setUserScore(e.target.value)}
                    placeholder="—"
                    className="w-full pl-3 pr-12 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500 tabular-nums disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 tabular-nums">
                    / {weight}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Statut</label>
                <div className="flex items-center gap-1">
                  {(['conforme', 'partial', 'non_conforme'] as EvaluationStatus[]).map(s => {
                    const active = userStatus === s;
                    const cfg = STATUS_CFG[s];
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setUserStatus(active ? '' : s)}
                        disabled={!criteriaValidated}
                        className={
                          active
                            ? `flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg border text-xs font-medium ${cfg.classes} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1 disabled:opacity-40 disabled:cursor-not-allowed`
                            : 'flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1 disabled:opacity-40 disabled:cursor-not-allowed'
                        }
                        aria-pressed={active}
                        title={cfg.label}
                      >
                        {cfg.icon}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="user-comment"
                className="block text-[11px] font-medium text-gray-700 mb-1"
              >
                Commentaire
                <span className="ml-1.5 text-gray-400 font-normal">(sert à générer le RAO)</span>
              </label>
              <textarea
                id="user-comment"
                value={userComment}
                onChange={e => setUserComment(e.target.value)}
                rows={4}
                placeholder="Précisez ici votre lecture de l'offre, un point de vigilance, ou la raison d'un écart avec l'IA."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500 resize-none disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>

            <div className="flex items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={handleReset}
                disabled={!criteriaValidated || evaluation?.userScore === undefined}
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-600 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Réinitialiser
              </button>
              <div className="flex items-center gap-2">
                {aiScore !== undefined && (
                  <button
                    type="button"
                    onClick={() => {
                      setUserScore(String(aiScore));
                      setUserStatus(aiStatus ?? '');
                    }}
                    disabled={!criteriaValidated}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-violet-700 border border-violet-200 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <BookmarkSquareIcon className="w-3.5 h-3.5" />
                    Copier IA
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CheckBadgeIcon className="w-4 h-4" />
                  Valider
                </button>
              </div>
            </div>
          </fieldset>
        </section>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  count?: number;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, subtitle, count }) => (
  <div className="flex items-start gap-2">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="text-gray-500">{icon}</span>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {count !== undefined && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded text-[10px] font-semibold tabular-nums bg-gray-100 text-gray-600">
            {count}
          </span>
        )}
      </div>
      {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

interface ScoreBadgeProps {
  label: string;
  icon: React.ReactNode;
  score?: number;
  weight: number;
  status?: EvaluationStatus;
  tone: 'ai' | 'user';
}

const ScoreBadge: React.FC<ScoreBadgeProps> = ({ label, icon, score, weight, status, tone }) => {
  const empty = score === undefined;
  const accent =
    tone === 'ai'
      ? 'text-gray-700 bg-gray-50 border-gray-200'
      : 'text-violet-700 bg-violet-50 border-violet-200';
  return (
    <div
      className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 border rounded-lg ${
        empty ? 'text-gray-400 bg-gray-50 border-gray-200' : accent
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wide">{label}</div>
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-semibold tabular-nums leading-none">
            {empty ? '—' : score!.toFixed(1)}
          </span>
          <span className="text-[11px] tabular-nums opacity-80">/ {weight}</span>
        </div>
      </div>
      {status && (
        <span
          className={`ml-auto inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${STATUS_CFG[status].classes}`}
          title={STATUS_CFG[status].label}
        >
          {STATUS_CFG[status].icon}
        </span>
      )}
    </div>
  );
};

const OriginBadge: React.FC<{ origin: Citation['origin'] }> = ({ origin }) => {
  if (origin === 'ai') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-500 uppercase tracking-wide">
        <SparklesIcon className="w-2.5 h-2.5" />
        IA
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-violet-700 uppercase tracking-wide">
      <PencilSquareIcon className="w-2.5 h-2.5" />
      Manuel
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────
// Ajout manuel de citation
// ─────────────────────────────────────────────────────────────────

interface AddCitationInlineProps {
  files: UploadedFile[];
  onAdd: (text: string, sourceFileId: string, sourcePage: number) => void;
  disabled?: boolean;
}

const AddCitationInline: React.FC<AddCitationInlineProps> = ({ files, onAdd, disabled }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [fileId, setFileId] = useState(files[0]?.id ?? '');
  const [page, setPage] = useState<string>('');

  useEffect(() => {
    if (open && !fileId && files[0]?.id) setFileId(files[0].id);
  }, [open, files, fileId]);

  const canSubmit = text.trim() && fileId && Number(page) > 0;

  const submit = () => {
    if (!canSubmit) return;
    onAdd(text.trim(), fileId, Number(page));
    setText('');
    setPage('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-violet-700 border border-dashed border-violet-300 rounded-lg hover:bg-violet-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <PlusIcon className="w-3.5 h-3.5" />
        Ajouter une citation
      </button>
    );
  }

  return (
    <div className="mt-3 border border-violet-200 bg-violet-50/30 rounded-lg p-2.5 space-y-2">
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={3}
        autoFocus
        placeholder="Collez ici l'extrait…"
        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500 resize-none"
      />
      <div className="grid grid-cols-[1fr_80px] gap-2">
        <select
          value={fileId}
          onChange={e => setFileId(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
        >
          {files.length === 0 ? (
            <option value="">Aucun fichier</option>
          ) : (
            files.map(f => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))
          )}
        </select>
        <input
          type="number"
          min="1"
          value={page}
          onChange={e => setPage(e.target.value)}
          placeholder="Page"
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500 tabular-nums"
        />
      </div>
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setText('');
            setPage('');
          }}
          className="px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Ajouter
        </button>
      </div>
    </div>
  );
};

export default EvaluationEditor;
